/**
 * @file selection.ts
 * @description Что выбрано в конструкторе сайта и как это лежит в адресе
 * (phase19.16 Р1-6а; uiux-builder-shell.md §4). Адрес — источник правды:
 * ссылку на раздел или блок можно переслать, «назад» браузера работает.
 *
 * `/shop/<slug>/site/builder?page=<id>|screen=<экран>&sel=block:<id>|header|
 * footer|page&panel=<раздел>[.<вкладка>]&group=<группа>&version=<N>`
 *  - `page` — страница конструктора, `screen` — экран магазина (каталог,
 *    корзина…: у них меняется только вид); нет обоих — главная;
 *  - `sel` — выбор на странице (блок, шапка, подвал; нет — настройки
 *    страницы); у экрана магазина — только шапка или подвал;
 *  - `panel` — раздел «Весь сайт»; пока он открыт, справа он, а не `sel`;
 *  - `group` — группа настроек в панели (SB122, основа SB121): панель её
 *    раскрывает и прокручивает к ней. Смена раздела, вкладки или выбора без
 *    явной группы её сбрасывает (`useBuilderSelection`, `builderLink`).
 * Разделы и вкладки — реестром (OCP): новая вкладка — строка в `PANEL_SUBS`.
 *
 * phase19.16 Р2а: раздел «Каталог» (вкладки «Список» и «Карточка товара»);
 * экран «Страница товара» — шаблон из блоков, на нём блок выбирается, как на
 * странице (`SCREENS_WITH_BLOCKS`).
 *
 * SB120 этап 4: раздел «Вид корзины» (`panel=cart`) — с открытой корзиной на
 * канве; в списке «Весь сайт» его нет (uiux-cart §3, вариант «в»): он
 * открывается на экране «Корзина», ссылкой из «Шапки» и нажатием по корзине
 * на канве.
 */

import type { PreviewScreenKey } from "../site-view/preview";

/** Разделы «Весь сайт» (+ `pages` — список страниц, старый адрес «Страницы сайта»). */
export const BUILDER_PANELS = ["appearance", "designs", "menu", "settings", "seo", "counters", "blog", "catalog", "cart", "history", "pages"] as const;
export type BuilderPanelKey = (typeof BUILDER_PANELS)[number];

/** Вкладки разделов; первая — по умолчанию. */
export const PANEL_SUBS: Readonly<Partial<Record<BuilderPanelKey, readonly string[]>>> = {
  appearance: ["colors", "fonts", "shapes", "backdrop", "designs"],
  menu: ["header", "footer", "phone", "crumbs"],
  settings: ["brand", "contacts", "socials", "footer", "seller", "access"],
  catalog: ["list", "card"],
  // Р3г: что показывает канва — баннер или окно «Настроить» (uiux-r3 §3.6).
  counters: ["banner", "settings"],
};

/** Экраны магазина в конструкторе (у них нет блоков — меняется только вид). */
export const BUILDER_SCREENS = ["catalog", "product", "cart", "checkout", "account", "blog"] as const satisfies readonly PreviewScreenKey[];
export type BuilderScreenKey = (typeof BUILDER_SCREENS)[number];

/** Экраны, у которых есть свои блоки (шаблон страницы товара, Р2а). */
export const SCREENS_WITH_BLOCKS: readonly BuilderScreenKey[] = ["product"];

export type CanvasAreaKey = "header" | "footer";

/**
 * Экран «Корзина» — ссылка из «Шапки» и из проверки публикации. Раздел не
 * задаётся: экран сам открывает «Вид корзины» (`SCREEN_PANELS`); `panel=cart`
 * — только нажатие по корзине на канве и строка в структуре экрана.
 */
export const CART_LOOK_SELECTION: Readonly<Partial<BuilderSelection>> = { pageId: null, screen: "cart", panel: null, sub: null, sel: { kind: "page" } };

/** Выбор на странице. */
export type BuilderSel = { kind: "page" } | { kind: "block"; id: string } | { kind: "area"; area: CanvasAreaKey };

export interface BuilderSelection {
  /** Страница; `null` — главная (или открыт экран магазина). */
  pageId: string | null;
  screen: BuilderScreenKey | null;
  sel: BuilderSel;
  panel: BuilderPanelKey | null;
  /** Вкладка раздела (`colors`, `header`, `brand`); `null` — первая. */
  sub: string | null;
  /** Открытая версия в «Истории». */
  version: number | null;
  /** Группа настроек панели (`?group=<id>`, SB122); `null` — нет. */
  group: string | null;
}

export const EMPTY_SELECTION: BuilderSelection = {
  pageId: null,
  screen: null,
  sel: { kind: "page" },
  panel: null,
  sub: null,
  version: null,
  group: null,
};

const isPanel = (v: string): v is BuilderPanelKey => (BUILDER_PANELS as readonly string[]).includes(v);
const isScreen = (v: string | null): v is BuilderScreenKey => v !== null && (BUILDER_SCREENS as readonly string[]).includes(v);

function parseSel(raw: string | null): BuilderSel {
  if (raw === "header" || raw === "footer") return { kind: "area", area: raw };
  if (raw?.startsWith("block:") && raw.length > 6) return { kind: "block", id: raw.slice(6) };
  return { kind: "page" };
}

function parsePanel(raw: string | null): { panel: BuilderPanelKey | null; sub: string | null } {
  if (!raw) return { panel: null, sub: null };
  const [head = "", tail = null] = raw.split(".", 2);
  if (!isPanel(head)) return { panel: null, sub: null };
  const subs = PANEL_SUBS[head];
  return { panel: head, sub: tail && subs?.includes(tail) ? tail : null };
}

/** У экрана магазина (кроме шаблона товара) блоков нет — выбрать можно только шапку или подвал. */
function screenSel(screen: BuilderScreenKey | null, sel: BuilderSel): BuilderSel {
  return screen && !SCREENS_WITH_BLOCKS.includes(screen) && sel.kind === "block" ? { kind: "page" } : sel;
}

/** Ключ группы: латиница, цифры, дефис (`bottom`, `about`); иное — как будто его нет. */
const GROUP_RE = /^[a-z][a-z0-9-]{0,39}$/i;

/** Выбор из адреса; незнакомое — как будто его нет. */
export function parseSelection(params: URLSearchParams): BuilderSelection {
  const screen = params.get("screen");
  const version = Number(params.get("version"));
  return {
    pageId: isScreen(screen) ? null : params.get("page") || null,
    screen: isScreen(screen) ? screen : null,
    sel: screenSel(isScreen(screen) ? screen : null, parseSel(params.get("sel"))),
    ...parsePanel(params.get("panel")),
    version: Number.isInteger(version) && version > 0 ? version : null,
    group: GROUP_RE.test(params.get("group") ?? "") ? params.get("group") : null,
  };
}

/** Адрес выбора: только то, что отличается от умолчаний. */
export function selectionToParams(s: BuilderSelection): URLSearchParams {
  const q = new URLSearchParams();
  if (s.screen) q.set("screen", s.screen);
  else if (s.pageId) q.set("page", s.pageId);
  if (s.sel.kind === "block") q.set("sel", `block:${s.sel.id}`);
  if (s.sel.kind === "area") q.set("sel", s.sel.area);
  if (s.panel) q.set("panel", s.sub ? `${s.panel}.${s.sub}` : s.panel);
  if (s.panel === "history" && s.version) q.set("version", String(s.version));
  if (s.group) q.set("group", s.group);
  return q;
}

/**
 * Новый выбор пишется заменой адреса (выбор блока, вкладка) или новой
 * записью истории (сменились страница, экран или раздел — «назад» вернёт).
 */
export function isNavigation(prev: BuilderSelection, next: BuilderSelection): boolean {
  return prev.pageId !== next.pageId || prev.screen !== next.screen || prev.panel !== next.panel;
}

/**
 * Ссылка на конструктор: `builderLink("chaiberi", { panel: "settings", sub: "brand" })`.
 * Группа — только явно: `{ sel: { kind: "area", area: "footer" }, group: "bottom" }`.
 */
export function builderLink(shopSlug: string, patch: Partial<BuilderSelection> = {}): string {
  const qs = selectionToParams({ ...EMPTY_SELECTION, ...patch }).toString();
  return `/shop/${shopSlug}/site/builder${qs ? `?${qs}` : ""}`;
}

/** Старые адреса экранов вида сайта → выбор в конструкторе (перенаправления ≥ одного выпуска). */
export const LEGACY_SITE_ROUTES: Readonly<Record<string, Partial<BuilderSelection>>> = {
  appearance: { panel: "appearance", sub: "colors" },
  "header-footer": { sel: { kind: "area", area: "header" } },
  menu: { panel: "menu", sub: "header" },
  settings: { panel: "settings" },
  // `seo` — с Р3в отдельный раздел «Сайт → SEO» (SB145), не панель конструктора.
  blog: { panel: "blog" },
  history: { panel: "history" },
};

/** Выбор для старого адреса с его параметрами (`?section=`, `?version=`). */
export function legacySelection(route: string, params: URLSearchParams): Partial<BuilderSelection> {
  const base = LEGACY_SITE_ROUTES[route] ?? {};
  const section = params.get("section");
  const version = Number(params.get("version"));
  return {
    ...base,
    ...(route === "settings" && section && PANEL_SUBS.settings?.includes(section) ? { sub: section } : {}),
    ...(route === "history" && Number.isInteger(version) && version > 0 ? { version } : {}),
  };
}
